import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import { AuthContextType } from '../../../../hooks/useAuth';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../gql/queries/orders/predefinedData';
import { resolveFreshRegisterDate } from '../../../../utils/freshRegisterDate';
import { selectActiveOperatorsAndVehicles } from '../../../../gql/selectors/actions';
import { buildRegisteredByOperators } from '../../../../utils/registeredByOperators';
import { claimDraftScope } from '../../../../utils/draftOwnership';
import {
  ACCIDENT_CREATION_SCOPE,
  ACCIDENT_MEDIA_SCOPE,
  accidentCreationOwnerId,
} from '../../../../utils/draftScopes';
import { readAccidentCreationDraft } from '../../../../utils/accidentCreationDraft';

// Data static
import { groupLanes, groupTracks } from '../../../../data/orders';

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { classifications },
        },
        {
          data: { predefinedOrders },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
      ]);

      return {
        ...data,
        capitols,
        classifications,
        groupTracks,
        groupLanes,
        predefinedOrders,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Accident loading error:', err);
    return true;
  }
};

export const NewAccidentLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // 🔴 Dueño al llegar (S4 de la auditoría de accidentes; spec 2026-11-02
  // wizard-draft-ownership, I1): el alta es de la OT/campaña de la URL. Con el
  // alta de P1 a medias, abrir a mano `…/P2/new-accident` enseñaba el
  // borrador de P1 y lo creaba BAJO P1. Síncrono y antes de cualquier
  // `await`: si el borrador es de otro padre (o de nadie) se descarta con su
  // croquis en curso. Las carpetas de OPFS no se tocan (Ruling 5).
  const owner = accidentCreationOwnerId(params.id);
  claimDraftScope(ACCIDENT_CREATION_SCOPE, owner);
  claimDraftScope(ACCIDENT_MEDIA_SCOPE, owner);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // La copia del paso 1, SOLO si es de este padre (`orderParentId`): el
  // cinturón del reclamo de arriba.
  const sessionStorageData = readAccidentCreationDraft(params.id);
  const loaderToResolve = await loadFormOptions(userData);
  const nameOperator = `${userData?.user?.user?.name} ${
    userData?.user?.user.surName.split(' ')[0]
  }`;

  // Operarios para el dropdown "Operario que registra".
  let registeredByOperators;
  try {
    const { operators } = await selectActiveOperatorsAndVehicles(true);
    registeredByOperators = buildRegisteredByOperators(operators, userData);
  } catch {
    registeredByOperators = buildRegisteredByOperators([], userData);
  }

  if (sessionStorageData) {
    const combinedData = {
      // team field removed - users no longer have teamIDs
      ...loaderToResolve,
      ...sessionStorageData,
      operator: nameOperator,
      operators: registeredByOperators,
      // Después del borrador, para ganarle: su fecha pudo quedar congelada
      // en una visita anterior (ver `resolveFreshRegisterDate`).
      ...resolveFreshRegisterDate(sessionStorageData),
    };

    return combinedData;
  }

  const combinedData = {
    // team field removed - users no longer have teamIDs
    ...loaderToResolve,
    operator: nameOperator,
    operators: registeredByOperators,
    // También sin borrador: así el marcador existe desde la primera visita.
    ...resolveFreshRegisterDate(undefined),
  };

  return combinedData;
};

  return { data: load() };
};
